import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  getTechniciansApi,
  createTechnicianApi,
  updateTechnicianApi,
  deleteTechnicianApi,
} from '../api/techniciansApi';
import { APP_CONFIG } from '../constants/config';
import { getServicesApi } from '../api/serviceApi';

interface Service {
  id: string;
  name: string;
}

interface Technician {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  supportedServiceType: 'US' | 'Xray';
  supportedServiceIds: string[];
  defaultConcurrency: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface NewTechnicianForm {
  name: string;
  email: string;
  phone: string;
  supportedServiceType: 'US' | 'Xray';
  supportedServiceIds: string[];
  defaultConcurrency: number;
}

const Technicians: React.FC = () => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTech, setEditingTech] = useState<Technician | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<NewTechnicianForm>({
    name: '',
    email: '',
    phone: '',
    supportedServiceType: 'US',
    supportedServiceIds: [],
    defaultConcurrency: 1,
  });

  const serviceTypeOptions = [
    { value: 'US', label: 'אולטרסאונד', disabled: false },
    { value: 'Xray', label: 'רנטגן', disabled: !APP_CONFIG.ENABLE_XRAY },
  ];

  useEffect(() => {
    loadTechnicians();
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const data = await getServicesApi();
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadTechnicians = async () => {
    try {
      setLoading(true);
      const data = await getTechniciansApi();
      setTechnicians(data);
    } catch (error) {
      console.error('Error loading technicians:', error);
      toast.error('שגיאה בטעינת טכנאים');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTechnician = () => {
    setEditingTech(null);
    setServiceSearchTerm('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      supportedServiceType: 'US',
      supportedServiceIds: [],
      defaultConcurrency: 1,
    });
    setShowModal(true);
  };

  const handleEditTechnician = (tech: Technician) => {
    setEditingTech(tech);
    setServiceSearchTerm('');
    setFormData({
      name: tech.name,
      email: tech.email || '',
      phone: tech.phone || '',
      supportedServiceType: tech.supportedServiceType,
      supportedServiceIds: tech.supportedServiceIds || [],
      defaultConcurrency: tech.defaultConcurrency,
    });
    setShowModal(true);
  };

  const handleSaveTechnician = async () => {
    if (!formData.name.trim()) {
      toast.error('נא להזין שם טכנאי');
      return;
    }
    
    if (formData.supportedServiceIds.length === 0) {
      toast.error('נא לבחור לפחות שירות אחד');
      return;
    }

    try {
      setLoading(true);
      
      if (editingTech) {
        // Update existing
        await updateTechnicianApi(editingTech.id, {
          name: formData.name,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          supportedServiceType: formData.supportedServiceType,
          supportedServiceIds: formData.supportedServiceIds,
          defaultConcurrency: formData.defaultConcurrency,
        });
        toast.success('טכנאי עודכן בהצלחה');
      } else {
        // Add new
        await createTechnicianApi({
          name: formData.name,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          supportedServiceType: formData.supportedServiceType,
          supportedServiceIds: formData.supportedServiceIds,
          defaultConcurrency: formData.defaultConcurrency,
        });
        toast.success('טכנאי חדש נוסף בהצלחה');
      }

      await loadTechnicians();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving technician:', error);
      toast.error(error.message || 'שגיאה בשמירת טכנאי');
    } finally {
      setLoading(false);
    }
  };

  const toggleTechnicianStatus = async (tech: Technician) => {
    try {
      setLoading(true);
      await updateTechnicianApi(tech.id, {
        isActive: !tech.isActive,
      });
      toast.success(`טכנאי ${tech.isActive ? 'הושבת' : 'הופעל'} בהצלחה`);
      await loadTechnicians();
    } catch (error: any) {
      console.error('Error toggling status:', error);
      toast.error(error.message || 'שגיאה בעדכון סטטוס');
    } finally {
      setLoading(false);
    }
  };

  const getServiceTypeLabel = (type: string) => {
    return serviceTypeOptions.find(opt => opt.value === type)?.label || type;
  };

  const filteredTechnicians = technicians.filter(tech => {
    const matchesSearch =
      tech.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.phone?.includes(searchTerm);
    
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && tech.isActive) ||
      (filterStatus === 'inactive' && !tech.isActive);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6" dir="rtl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800">ניהול טכנאים</h1>
        <p className="text-slate-600 mt-2">
          ניהול פרופילים של טכנאים, התמחויות ויכולות. הקצאת טכנאים לימים מתבצעת ב"ניהול שעות עבודה"
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <svg
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="חיפוש לפי שם, אימייל או טלפון..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              הכל ({technicians.length})
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              פעילים ({technicians.filter(t => t.isActive).length})
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'inactive'
                  ? 'bg-red-600 text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              לא פעילים ({technicians.filter(t => !t.isActive).length})
            </button>
          </div>

          <button
            onClick={handleAddTechnician}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            הוסף טכנאי
          </button>
        </div>
      </div>

      {/* Technicians Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTechnicians.map((tech) => (
          <div
            key={tech.id}
            className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow ${
              !tech.isActive ? 'opacity-60' : ''
            }`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">{tech.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{tech.name}</h3>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                        tech.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {tech.isActive ? 'פעיל' : 'לא פעיל'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                {tech.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    {tech.email}
                  </div>
                )}
                {tech.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    {tech.phone}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <span className="text-xs font-semibold text-slate-500 uppercase">
                    שירותים נתמכים ({tech.supportedServiceIds?.length || 0})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {tech.supportedServiceIds?.map((serviceId) => {
                    const service = services.find(s => s.id === serviceId);
                    return service ? (
                      <span
                        key={serviceId}
                        className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded"
                      >
                        {service.name}
                      </span>
                    ) : null;
                  })}
                  {(!tech.supportedServiceIds || tech.supportedServiceIds.length === 0) && (
                    <span className="text-xs text-slate-400">לא הוגדרו שירותים</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <span className="text-sm font-semibold text-slate-700">
                    קיבולת: <span className="text-indigo-600">{tech.defaultConcurrency}</span>
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditTechnician(tech)}
                    className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-900 font-medium border border-indigo-300 rounded hover:bg-indigo-50"
                    disabled={loading}
                  >
                    ערוך
                  </button>
                  <button
                    onClick={() => toggleTechnicianStatus(tech)}
                    className={`px-3 py-1 text-sm font-medium border rounded ${
                      tech.isActive
                        ? 'text-red-600 border-red-300 hover:bg-red-50'
                        : 'text-green-600 border-green-300 hover:bg-green-50'
                    }`}
                    disabled={loading}
                  >
                    {tech.isActive ? 'השבת' : 'הפעל'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTechnicians.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg
            className="w-16 h-16 mx-auto text-slate-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <p className="text-slate-500 text-lg">לא נמצאו טכנאים</p>
          <p className="text-slate-400 text-sm mt-2">
            {searchTerm || filterStatus !== 'all' ? 'נסה לשנות את החיפוש או הסינון' : 'לחץ על "הוסף טכנאי" להתחלה'}
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-800">
                {editingTech ? 'עריכת טכנאי' : 'הוספת טכנאי חדש'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">שם טכנאי *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="הזן שם מלא"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">אימייל</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="example@clinic.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">טלפון</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="050-1234567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  סוג שירות *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {serviceTypeOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center justify-center gap-2 p-3 border-2 rounded-lg transition-all ${
                        option.disabled
                          ? 'opacity-50 cursor-not-allowed bg-slate-100'
                          : formData.supportedServiceType === option.value
                          ? 'border-indigo-600 bg-indigo-50 cursor-pointer'
                          : 'border-slate-300 hover:bg-slate-50 cursor-pointer'
                      }`}
                    >
                      <input
                        type="radio"
                        name="supportedServiceType"
                        value={option.value}
                        checked={formData.supportedServiceType === option.value}
                        disabled={option.disabled}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            supportedServiceType: e.target.value as 'US' | 'Xray',
                          })
                        }
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm font-medium text-slate-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  שירותים נתמכים *
                </label>
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="חיפוש שירות..."
                    value={serviceSearchTerm}
                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="border border-slate-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {services
                    .filter(service => {
                      // Filter by search term
                      if (serviceSearchTerm) {
                        return service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase());
                      }
                      return true; // Show all if no search
                    })
                    .sort((a, b) => a.name.localeCompare(b.name, 'he'))
                    .map((service) => (
                      <label
                        key={service.id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.supportedServiceIds.includes(service.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                supportedServiceIds: [...formData.supportedServiceIds, service.id],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                supportedServiceIds: formData.supportedServiceIds.filter(
                                  (id) => id !== service.id
                                ),
                              });
                            }
                          }}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                        <span className="text-sm text-slate-700">{service.name}</span>
                      </label>
                    ))}
                  {services.length === 0 && (
                    <div className="text-sm text-slate-400 text-center py-4">
                      לא נמצאו שירותים
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  בחר את השירותים שהטכנאי יכול לבצע
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  קיבולת ברירת מחדל (concurrent appointments)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.defaultConcurrency}
                  onChange={(e) =>
                    setFormData({ ...formData, defaultConcurrency: parseInt(e.target.value) || 1 })
                  }
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  כמה תורים יכולים להתקיים במקביל באותה שעה
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 text-slate-700 bg-slate-200 rounded-lg hover:bg-slate-300 font-medium"
                disabled={loading}
              >
                ביטול
              </button>
              <button
                onClick={handleSaveTechnician}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'שומר...' : editingTech ? 'שמור שינויים' : 'הוסף טכנאי'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Technicians;
